/**
 * Panel displaying the user's typed and selected keyword responses alongside expected answers.
 *
 * @module components/sim/takeSim/TypingAndSelection
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from "prop-types";
import {useStyles} from "./TakeSimResult.js";
import {Divider, Grid, Paper,} from "@material-ui/core";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import {connect} from "react-redux";

/**
 * A React functional component that displays a user's typing and selection inputs
 * along with their matching or mismatching status. It processes and formats phone numbers,
 * checks the validity of numerical strings, and visualizes input data against expected target
 * values.
 *
 * @param {Object} userResult - The user's result data containing input comparisons.
 * @param {Array} userResult.typing_inputs - An array of objects representing typing input data.
 * @param {Array} userResult.data_inputs - An array of objects representing selection data inputs.
 * @param {Object} userResult.scripts - Contains scripts including title metadata.
 *
 * @returns {JSX.Element} A styled component rendering input data and comparison results.
 */
const TypingAndSelection = ({
    userResult,
}) => {
    const classes = useStyles();
    const numberList = "0123456789";

    /**
     * Validates whether a given string contains only characters present in a predefined list of
     * numbers and ensures that the string length exceeds 9 characters.
     *
     * @function
     * @param {string} str - The input string to be checked.
     * @returns {boolean} Returns true if the input string consists solely of valid number
     *     characters and its length is greater than 9, otherwise false.
     */
    const checkNumber = (str) => {
        let x = str.split("").every((ele) => numberList.includes(ele));
        return x && str.length > 9;
    };

    /**
     * Formats a given phone number string into a standardized format.
     *
     * The function strips any non-digit characters from the input string, validates if it matches
     * the expected phone number pattern, and formats it accordingly. Supports optional
     * international dialing code (+1 for US/Canada).
     *
     * @param {string} phoneNumberString - The raw phone number string to be formatted.
     * @returns {(string|null)} - The formatted phone number as a string, or null if the input is
     *     invalid.
     */
    const formatPhoneNumber = (phoneNumberString) => {
        let cleaned = ("" + phoneNumberString).replace(/\D/g, "");
        let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            let intlCode = match[1] ? "+1 " : "";
            return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
        }
        return null;
    };

    return (
        <Grid container className={classes.girdContainer} item md={4}>
            <Paper style={{width: "100%", borderRadius: "1%"}}>
                <div className={classes.root}>
                    <h2>
                        {userResult?.scripts?.length &&
                            userResult?.scripts[0]?.sim_id?.title}
                    </h2>
                </div>
                <Divider variant="fullWidth"/>
                <div className={classes.root}>
                    <h2 style={{marginBottom: "0.7rem", fontSize: '1.2rem'}}>Typing</h2>
                    {userResult.typing_inputs?.map((ele, idx) => (
                        <div
                            key={idx}
                            className={
                                ele.input_value === ele.target_value
                                    ? classes.patientDataMatched
                                    : classes.patientData
                            }
                        >
                            {ele.input_value === ele.target_value ? (
                                <>
                                    <span>{ele.field_title}</span><br/>
                                    <div className={classes.flex}>
                                        <CheckCircleRoundedIcon
                                            className={classes.success}
                                        ></CheckCircleRoundedIcon>{" "}
                                        <span className={classes.bold}>
                                            {ele.target_value.startsWith("<a ") ? (
                                              <span
                                                  dangerouslySetInnerHTML={{
                                                      __html: ele.target_value,
                                                  }}
                                              ></span>
                                          ) : checkNumber(ele.target_value) ? (
                                              formatPhoneNumber(ele.target_value)
                                          ) : (
                                              ele.target_value
                                          )}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <span>{ele.field_title}</span><br/>
                                    <div className={classes.flex}>
                                        <CancelRoundedIcon
                                            className={classes.failed}
                                        ></CancelRoundedIcon>
                                        <span className={classes.script}>
                                            {ele.input_value}
                                        </span>
                                    </div>
                                    <span
                                        className={classes.bold}
                                        style={{
                                            marginTop: ".5rem",
                                            display: "inline-block",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {ele.target_value.startsWith("<a ") ? (
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: ele.target_value,
                                                }}
                                            ></span>
                                        ) : checkNumber(ele.target_value) ? (
                                            formatPhoneNumber(ele.target_value)
                                        ) : (
                                            ele.target_value
                                        )}
                                    </span>
                                </>
                            )}
                        </div>
                    ))}
                    <h4 style={{marginBottom: "0.7rem", fontSize: '1.2rem'}}>Data Selection</h4>
                    {userResult.data_inputs?.map((ele, i) => (
                        <div
                            key={i}
                            className={
                                ele.input_value === ele.target_value
                                    ? classes.patientDataMatched
                                    : classes.patientData
                            }
                        >
                            {ele.input_value === ele.target_value ? (
                                <>
                                    <span>{ele.field_title}</span><br/>
                                    <div className={classes.flex}>
                                        <CheckCircleRoundedIcon
                                            className={classes.success}
                                        ></CheckCircleRoundedIcon>
                                        <span className={classes.bold}>{ele.target_value}</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <span>{ele.field_title}</span><br/>
                                    <div className={classes.flex}>
                                        <CancelRoundedIcon
                                            className={classes.failed}
                                        ></CancelRoundedIcon>{" "}
                                        {ele.input_value}
                                        <span className={classes.script}></span>
                                    </div>
                                    <span
                                        className={classes.bold}
                                        style={{marginTop: ".5rem", display: "inline-block"}}
                                    >
                                        {ele.target_value}
                                    </span>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </Paper>
        </Grid>
    )
}

/**
 * PropTypes for the TypingAndSelection component.
 * Defines the expected types and requirements for the properties
 * supplied to the component to ensure proper usage and type safety.
 */
TypingAndSelection.propTypes = {
    userResult: PropTypes.object,
};

/**
 * Maps the necessary parts of the Redux state to the props of a React component.
 *
 * This function extracts specific properties from the application's state object
 * and makes them available as props to the connected component. It is commonly
 * used in conjunction with the `connect` function from the `react-redux` library
 * to link the Redux store with a React component.
 *
 * @param {Object} state - The entire Redux store state.
 * @returns {Object} An object containing the mapped state properties:
 * - `userResponse` - Represents the user's response data from the `sim` slice of the state.
 * - `userResult` - Represents the user's result data from the `sim` slice of the state.
 */
const mapStateToProps = (state) => ({
    userResponse: state.sim.userResponse,
    userResult: state.sim.userResult,
});

export default connect(mapStateToProps, null)(TypingAndSelection);
